/**
 * Centralized workspace URL construction.
 *
 * All workspace-scoped URLs in the app must come from `buildWorkspaceUrl` (or
 * the `buildUrl` exposed by `WorkspaceContext`, which is a thin wrapper
 * capturing the current workspace's slug). Components MUST NOT template
 * `/workspaces/${...}` strings inline — keeping the templating in one place
 * means changes to the URL scheme touch one file.
 *
 * The `WorkspaceUrlTarget` discriminated union mirrors the routes registered
 * in `router.tsx`. TypeScript exhaustiveness on the `kind` field guarantees
 * every variant is handled and that adding a new route requires extending
 * both this union and the switch below in lockstep.
 */

export type WorkspaceUrlTarget =
  // ── Sections (no params) ─────────────────────────────────────────
  | { kind: 'workspace-root' }       // `/workspaces/:slug`
  | { kind: 'dashboard' }
  | { kind: 'work' }
  | { kind: 'interests' }
  | { kind: 'teams' }                // "Your teams" listing
  | { kind: 'assistant' }
  | { kind: 'profile' }
  | { kind: 'dependencies' }
  | { kind: 'priorities' }
  // Data section
  | { kind: 'data-explore' }
  | { kind: 'workflows' }
  | { kind: 'workflow-template-new' }
  // Workspace admin
  | { kind: 'workspace-hub' }
  | { kind: 'settings' }
  | { kind: 'data-sources' }
  | { kind: 'invitations' }
  | { kind: 'pending-changes' }
  | { kind: 'history' }
  | { kind: 'tools' }
  | { kind: 'consolidation' }
  | { kind: 'tidy' }
  // ── Detail / parameterized routes ────────────────────────────────
  | { kind: 'task'; taskId: string }
  | { kind: 'person'; personId: string }
  | { kind: 'team'; teamId: string }
  | { kind: 'outcome'; outcomeId: string }
  | { kind: 'project'; projectId: string }
  | { kind: 'decision'; decisionId: string }
  | { kind: 'problem'; problemId: string }
  | { kind: 'product'; productId: string }
  | { kind: 'stakeholder'; stakeholderId: string }
  | { kind: 'workflow-instance'; instanceId: string }
  | { kind: 'resource'; resourceId: string }
  | { kind: 'resource-group'; groupId: string }
  | { kind: 'data-source-import'; dataSourceId: string }
  | { kind: 'workflow-template-edit'; templateId: string }
  /**
   * Workflow-instance list, optionally filtered to a specific template via
   * a `?template=...` query string. Currently routes through to the legacy
   * `/workflow-instances` path; the redirect into the data-explore page is
   * router-level concern.
   */
  | { kind: 'workflow-instances-list'; templateFilter?: string };

/**
 * Build a URL for a workspace-scoped target.
 *
 * `workspaceSlugOrId` is opaque — pass either the slug (preferred for
 * navigation; backend resolves via the slug index) or the canonical UUID
 * (fallback; backend looks up by primary key). The resulting URL has the
 * value placed verbatim into the `:workspaceId` slot of the route.
 */
export const buildWorkspaceUrl = (
  workspaceSlugOrId: string,
  target: WorkspaceUrlTarget,
): string => {
  const base = `/workspaces/${workspaceSlugOrId}`;
  switch (target.kind) {
    case 'workspace-root':         return base;
    case 'dashboard':              return `${base}/dashboard`;
    case 'work':                   return `${base}/work`;
    case 'interests':              return `${base}/interests`;
    case 'teams':                  return `${base}/teams`;
    case 'assistant':              return `${base}/assistant`;
    case 'profile':                return `${base}/profile`;
    case 'dependencies':           return `${base}/dependencies`;
    case 'priorities':             return `${base}/priorities`;
    case 'data-explore':           return `${base}/data/explore`;
    case 'workflows':              return `${base}/data/workflows`;
    case 'workflow-template-new':  return `${base}/data/workflows/templates/new`;
    case 'workspace-hub':          return `${base}/workspace`;
    case 'settings':               return `${base}/workspace/settings`;
    case 'data-sources':           return `${base}/workspace/data-sources`;
    case 'invitations':            return `${base}/workspace/invitations`;
    case 'pending-changes':        return `${base}/workspace/pending-changes`;
    case 'history':                return `${base}/workspace/history`;
    case 'tools':                  return `${base}/workspace/tools`;
    case 'consolidation':          return `${base}/workspace/tools/consolidation`;
    case 'tidy':                   return `${base}/workspace/tools/tidy`;
    case 'task':                   return `${base}/tasks/${target.taskId}`;
    case 'person':                 return `${base}/people/${target.personId}`;
    case 'team':                   return `${base}/teams/${target.teamId}`;
    case 'outcome':                return `${base}/outcomes/${target.outcomeId}`;
    case 'project':                return `${base}/projects/${target.projectId}`;
    case 'decision':               return `${base}/decisions/${target.decisionId}`;
    case 'problem':                return `${base}/problems/${target.problemId}`;
    case 'product':                return `${base}/products/${target.productId}`;
    case 'stakeholder':            return `${base}/stakeholders/${target.stakeholderId}`;
    case 'workflow-instance':      return `${base}/workflow-instances/${target.instanceId}`;
    case 'resource':               return `${base}/resources/${target.resourceId}`;
    case 'resource-group':         return `${base}/resource-groups/${target.groupId}`;
    case 'data-source-import':     return `${base}/workspace/data-sources/${target.dataSourceId}/import`;
    case 'workflow-template-edit': return `${base}/data/workflows/templates/${target.templateId}/edit`;
    case 'workflow-instances-list': {
      const path = `${base}/workflow-instances`;
      return target.templateFilter
        ? `${path}?template=${encodeURIComponent(target.templateFilter)}`
        : path;
    }
  }
};

/**
 * Pick the URL-friendly identifier for a workspace: prefer the slug if
 * claimed, fall back to the UUID. Used by code that has a workspace object
 * but no `WorkspaceContext` (e.g. the workspace list page).
 */
export const workspaceUrlId = (workspace: { slug?: string | null; id: string }): string =>
  workspace.slug ?? workspace.id;

/**
 * Subset of `WorkspaceUrlTarget['kind']` for variants that take no extra
 * parameters. Used by static navigation config (`siteRoutes.ts`) so a
 * `SiteRoute` can declare its target without supplying ids that don't apply.
 */
export type WorkspaceUrlSectionKind =
  | 'workspace-root'
  | 'dashboard'
  | 'work'
  | 'interests'
  | 'teams'
  | 'assistant'
  | 'profile'
  | 'dependencies'
  | 'priorities'
  | 'data-explore'
  | 'workflows'
  | 'workflow-template-new'
  | 'workspace-hub'
  | 'settings'
  | 'data-sources'
  | 'invitations'
  | 'pending-changes'
  | 'history'
  | 'tools'
  | 'consolidation'
  | 'tidy';
